// src/scenes/sitemanager/LaborHoursManagement.jsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
    CircularProgress,
    useTheme,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@mui/material";
import { Edit, Add } from "@mui/icons-material";
import { tokens } from "../../theme";
import { useAuth } from "../../context/AuthContext";

const API_LABOR = "https://figazuresql.swancreate.com/api/swa/FINANCE_LaborHours";
const API_SCHOOL = "https://figazuresql.swancreate.com/api/swa/SchoolMst";

export default function LaborHoursManagement() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { allocations, isSchoolUser } = useAuth();
    const allowedIds = allocations.map(a => a.SchoolId);

    // data & lookup state
    const [data, setData] = useState([]);
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);

    // sorting & filtering
    const [sortField, setSortField] = useState("SchoolName");
    const [sortAsc, setSortAsc] = useState(true);
    const [filterSchoolId, setFilterSchoolId] = useState("");
    const [filterMonth, setFilterMonth] = useState("");
    const [filterYear, setFilterYear] = useState("");

    // modal & form state
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        apiid: null,
        SchoolId: "",
        dateStr: "",
        LaborHours: "",
        Type: "Monthly",
        isActive: 1
    });

    // load schools + labor hours
    useEffect(() => {
        async function loadAll() {
            setLoading(true);
            try {
                const [sRes, lRes] = await Promise.all([
                    axios.get(API_SCHOOL),
                    axios.get(API_LABOR)
                ]);

                // limit schools to manager�s allocations
                const sorted = sRes.data
                    .filter(s => !isSchoolUser || allowedIds.includes(s.SchoolId))
                    .sort((a, b) => a.SchoolName.localeCompare(b.SchoolName));
                setSchools(sorted);

                // join labor rows with SchoolName
                const joined = lRes.data
                    .filter(r => !isSchoolUser || allowedIds.includes(r.SchoolId))
                    .map(r => ({
                        ...r,
                        SchoolName:
                            sorted.find(s => s.SchoolId === r.SchoolId)?.SchoolName || ""
                    }));
                setData(joined);
            } catch (err) {
                console.error("Load error:", err);
            } finally {
                setLoading(false);
            }
        }
        loadAll();
    }, [allocations, isSchoolUser]);

    // sorting handler
    const handleSort = field => {
        setSortField(field);
        setSortAsc(prev => (field === sortField ? !prev : true));
    };
    const sortedData = [...data].sort((a, b) => {
        const va = a[sortField] ?? "";
        const vb = b[sortField] ?? "";
        if (va < vb) return sortAsc ? -1 : 1;
        if (va > vb) return sortAsc ? 1 : -1;
        return 0;
    });

    // filtering
    const filtered = sortedData.filter(r =>
        (!filterSchoolId || r.SchoolId === filterSchoolId) &&
        (!filterMonth || r.Month === filterMonth) &&
        (!filterYear || r.Year === filterYear)
    );

    const months = Array.from(new Set(data.map(r => r.Month))).filter(Boolean);
    const years = Array.from(new Set(data.map(r => r.Year))).filter(Boolean);

    // open edit dialog
    const handleEdit = row => {
        setForm({
            apiid: row.apiid,
            SchoolId: row.SchoolId,
            dateStr: row.Date?.substr(0, 10) || "",
            LaborHours: row.LaborHours?.toString() || "",
            Type: row.Type || "Monthly",
            isActive: row.isActive ?? 1
        });
        setOpen(true);
    };

    // open add dialog
    const handleAdd = () => {
        setForm({
            apiid: null,
            SchoolId: schools[0]?.SchoolId || "",
            dateStr: "",
            LaborHours: "",
            Type: "Monthly",
            isActive: 1
        });
        setOpen(true);
    };

    const handleClose = () => setOpen(false);
    const handleChange = (field, val) => setForm(f => ({ ...f, [field]: val }));

    // save (POST/PUT)
    const handleSave = async () => {
        try {
            const { apiid, SchoolId, dateStr, LaborHours, Type } = form;
            const dateObj = new Date(dateStr);
            const payload = {
                SchoolId,
                SchoolName: schools.find(s => s.SchoolId === SchoolId)?.SchoolName || "",
                Date: dateStr,
                Month: dateObj.toLocaleString("default", { month: "long" }),
                Year: dateObj.getFullYear().toString(),
                LaborHours: parseFloat(LaborHours) || 0,
                Type,
                isActive: 1
            };
            const url = apiid ? `${API_LABOR}/${apiid}` : API_LABOR;

            if (apiid) await axios.put(url, payload);
            else await axios.post(API_LABOR, payload);

            // refresh
            setLoading(true);
            const lRes = await axios.get(API_LABOR);
            setData(lRes.data.map(r => ({
                ...r,
                SchoolName: schools.find(s => s.SchoolId === r.SchoolId)?.SchoolName || ""
            })));
            setLoading(false);
            handleClose();
        } catch (err) {
            console.error("Save error:", err);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={2}>
            {/* Header + Filters + Add */}
            <Box display="flex" alignItems="center" mb={2} gap={2}>
                <Typography variant="h5" flexGrow={1}>
                    Manage Labor Hours
                </Typography>

                <FormControl size="small">
                    <InputLabel>Filter School</InputLabel>
                    <Select
                        label="Filter School"
                        value={filterSchoolId}
                        onChange={e => setFilterSchoolId(e.target.value)}
                        sx={{ minWidth: 160 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {schools.map(s => (
                            <MenuItem key={s.SchoolId} value={s.SchoolId}>
                                {s.SchoolName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small">
                    <InputLabel>Filter Month</InputLabel>
                    <Select
                        label="Filter Month"
                        value={filterMonth}
                        onChange={e => setFilterMonth(e.target.value)}
                        sx={{ minWidth: 120 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {months.map(m => (
                            <MenuItem key={m} value={m}>
                                {m}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small">
                    <InputLabel>Filter Year</InputLabel>
                    <Select
                        label="Filter Year"
                        value={filterYear}
                        onChange={e => setFilterYear(e.target.value)}
                        sx={{ minWidth: 100 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {years.map(y => (
                            <MenuItem key={y} value={y}>
                                {y}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
                    Add Entry
                </Button>
            </Box>

            {/* Data Table */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead sx={{ backgroundColor: colors.primary[400] }}>
                        <TableRow>
                            <TableCell onClick={() => handleSort("SchoolName")} sx={{ cursor: "pointer", fontWeight: "bold" }}>
                                School
                            </TableCell>
                            <TableCell onClick={() => handleSort("Date")} sx={{ cursor: "pointer", fontWeight: "bold" }}>
                                Date
                            </TableCell>
                            <TableCell onClick={() => handleSort("Month")} sx={{ cursor: "pointer", fontWeight: "bold" }}>
                                Month
                            </TableCell>
                            <TableCell onClick={() => handleSort("Year")} sx={{ cursor: "pointer", fontWeight: "bold" }}>
                                Year
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Labor Hours</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                            <TableCell align="right" sx={{ fontWeight: "bold" }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.map((row, idx) => (
                            <TableRow
                                key={row.apiid}
                                sx={{ backgroundColor: idx % 2 === 0 ? theme.palette.action.hover : "inherit" }}
                            >
                                <TableCell>{row.SchoolName}</TableCell>
                                <TableCell>{row.Date?.substr(0, 10)}</TableCell>
                                <TableCell>{row.Month}</TableCell>
                                <TableCell>{row.Year}</TableCell>
                                <TableCell>{row.LaborHours}</TableCell>
                                <TableCell>{row.Type}</TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => handleEdit(row)}>
                                        <Edit />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Edit/Add Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
                <DialogTitle>{form.apiid ? "Edit Entry" : "New Entry"}</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>School</InputLabel>
                        <Select
                            label="School"
                            value={form.SchoolId}
                            onChange={e => handleChange("SchoolId", e.target.value)}
                        >
                            {schools.map(s => (
                                <MenuItem key={s.SchoolId} value={s.SchoolId}>
                                    {s.SchoolName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Date"
                        type="date"
                        size="small"
                        value={form.dateStr}
                        onChange={e => handleChange("dateStr", e.target.value)}
                        fullWidth
                    />
                    <TextField
                        label="Labor Hours"
                        size="small"
                        type="number"
                        inputProps={{ step: "0.01", min: 0 }}
                        value={form.LaborHours}
                        onChange={e => handleChange("LaborHours", e.target.value)}
                        fullWidth
                    />
                    <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                            label="Type"
                            value={form.Type}
                            onChange={e => handleChange("Type", e.target.value)}
                        >
                            <MenuItem value="Daily">Daily</MenuItem>
                            <MenuItem value="Weekly">Weekly</MenuItem>
                            <MenuItem value="Monthly">Monthly</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
